import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer } from 'recharts';

var PIE_COLORS = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

function AdminOverview({ showToast, user }) {
  var [overview, setOverview] = useState(null);
  var [companies, setCompanies] = useState([]);
  var [auctionData, setAuctionData] = useState(null);   // Wave X-1
  var [loading, setLoading] = useState(true);
  var [search, setSearch] = useState('');
  var [sortKey, setSortKey] = useState('carCount');
  var [sortDir, setSortDir] = useState('desc');
  var [planFilter, setPlanFilter] = useState('all');

  useEffect(function() {
    Promise.all([
      authFetch(API + '/admin/overview').then(function(r) { return r.json(); }),
      authFetch(API + '/admin/companies-stats').then(function(r) { return r.json(); }),
      authFetch(API + '/admin/auction-overview').then(function(r) { return r.json(); }).catch(function() { return null; })
    ]).then(function(results) {
      if (results[0].success) setOverview(results[0]);
      if (results[1].success) setCompanies(results[1].companies || []);
      if (results[2] && results[2].success) setAuctionData(results[2].data);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }, []);

  var switchTo = function(companyId) {
    authFetch(API + '/auth/switch-company', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId: companyId })
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) { localStorage.setItem('autodoc_token', d.token); if(d.user)sessionStorage.setItem('autodoc_user',JSON.stringify(d.user)); window.location.href = '/main/dashboard'; }
    }).catch(function() {});
  };

  var toggleSort = function(key) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  var filtered = companies.filter(function(c) {
    if (planFilter !== 'all' && (c.plan || 'basic') !== planFilter) return false;
    if (!search) return true;
    var s = search.toLowerCase();
    return (c.name || '').toLowerCase().indexOf(s) >= 0 || (c.bizNo || '').indexOf(s) >= 0;
  });
  filtered.sort(function(a, b) {
    var va = a[sortKey] || 0; var vb = b[sortKey] || 0;
    if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  var card = { background: '#fff', borderRadius: 12, border: '1px solid #eee', padding: 16, marginBottom: 12 };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>로딩 중...</div>;
  if (!overview) return <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>데이터 없음</div>;

  var s = overview.summary;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 12px' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800, color: '#111' }}>전체 현황</h2>

      {/* 상단 요약 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[
          [s.totalCompanies, '총 업체', '#6366f1'],
          [s.totalCars, '총 차량', '#3b82f6'],
          [s.thisMonthNew, '이번달 신규', '#10b981'],
          [s.thisMonthOcr, '이번달 OCR', '#f59e0b']
        ].map(function(item, i) {
          return (
            <div key={i} style={{ ...card, textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: item[2] }}>{(item[0] || 0).toLocaleString()}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{item[1]}</div>
            </div>
          );
        })}
      </div>

      {/* Wave X-1 — 경매 현황 (auction system overview) */}
      {auctionData && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '8px 0 12px', fontSize: 15, fontWeight: 800, color: '#1B3A5C' }}>
            {'🎯 경매 현황'}
          </h3>
          {/* 4-card row: 경매 / 외부 바이어 / 거래수수료 / 채팅 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            {/* 경매 카드 */}
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>{'🔨 활성 경매'}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1B3A5C' }}>{auctionData.auctions.active.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                {'대기 '+auctionData.auctions.pendingSelection+' · 24h 임박 '+auctionData.auctions.soonClosing24h}
              </div>
            </div>
            {/* 외부 바이어 카드 */}
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>{'👥 외부 바이어'}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{auctionData.buyers.active.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                {'전체 '+auctionData.buyers.total+' · 정지 '+auctionData.buyers.suspended+' · 대기 '+auctionData.buyers.pending}
              </div>
            </div>
            {/* 거래수수료 카드 */}
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>{'💰 거래수수료 미수'}</div>
              <div style={{
                fontSize: 24, fontWeight: 800,
                color: auctionData.fees.overdueD30 > 0 ? '#dc2626' : (auctionData.fees.overdueD15 > 0 ? '#f59e0b' : '#1B3A5C'),
              }}>{auctionData.fees.pendingTotal.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                {'D-7 임박 '+auctionData.fees.dueWithin7d+' · D+15 '+auctionData.fees.overdueD15+' · D+30 '+auctionData.fees.overdueD30}
              </div>
            </div>
            {/* 채팅 카드 */}
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>{'💬 채팅 (7d)'}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#8b5cf6' }}>{auctionData.chat.messagesLast7d.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                {'활성 대화 '+auctionData.chat.activeConversations}
              </div>
            </div>
          </div>
          {/* 매너 페널티 + 누적 수익 (1 row, 2 카드) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>{'⚠️ 매너 페널티 (지난 30일)'}</div>
              <div style={{ display: 'flex', gap: 24 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>{auctionData.manner.penaltyD15Last30d}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{'D+15 -2° 적용'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#dc2626' }}>{auctionData.manner.penaltyD30Last30d}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{'D+30 -5° 적용'}</div>
                </div>
              </div>
            </div>
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>{'💵 거래수수료 누적 수익'}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{Math.round((auctionData.fees.totalRevenue || 0) / 1000).toLocaleString() + 'K'}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{'paid 합계 ('+ auctionData.fees.totalRevenue.toLocaleString() +'원)'}</div>
            </div>
          </div>
        </div>
      )}

      {/* 차트 3개 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#222' }}>월별 차량 등록</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={(overview.monthlyTrend || []).map(function(t) { return { name: parseInt(t.month.split('-')[1]) + '월', 등록: t.count }; })}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={10} />
              <YAxis fontSize={10} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="등록" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#222' }}>업체별 매출</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={(overview.companySales || []).map(function(cs) { return { name: cs.name, value: Math.round((cs.totalSell || 0) / 10000) }; }).filter(function(d) { return d.value > 0; })}
                cx="50%" cy="50%" outerRadius={70} dataKey="value"
                label={function(p) { return p.name.slice(0, 4); }}>
                {(overview.companySales || []).map(function(_, i) { return <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />; })}
              </Pie>
              <Tooltip formatter={function(v) { return v.toLocaleString() + '만원'; }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#222' }}>OCR 사용량</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={(overview.ocrTrend || []).map(function(t) { return { name: parseInt(t.month.split('-')[1]) + '월', OCR: t.count }; })}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={10} />
              <YAxis fontSize={10} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="OCR" stroke="#f59e0b" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 알림 */}
      {overview.alerts && overview.alerts.length > 0 && (
        <div style={{ ...card, background: '#fffbeb', border: '1px solid #fef08a' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#92400e' }}>알림 ({overview.alerts.length})</div>
          {overview.alerts.map(function(a, i) {
            return <div key={i} style={{ fontSize: 12, color: '#78350f', padding: '4px 0', borderBottom: i < overview.alerts.length - 1 ? '1px solid #fef08a' : 'none' }}>{a.type === 'warn' ? '⚠️' : 'ℹ️'} {a.message}</div>;
          })}
        </div>
      )}

      {/* 업체별 현황 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#222' }}>업체별 현황 ({filtered.length})</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={planFilter} onChange={function(e) { setPlanFilter(e.target.value); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e0dfda', fontSize: 12 }}>
            <option value="all">전체 요금제</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="trial">Trial</option>
          </select>
          <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="업체 검색" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e0dfda', fontSize: 12, width: 160 }} />
        </div>
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
              {[['name', '업체명'], ['plan', '요금제'], ['carCount', '차량'], ['monthOcr', 'OCR(월)'], ['pendingCount', '미처리'], ['userCount', '사용자'], ['lastActivity', '마지막 활동']].map(function(col) {
                return <th key={col[0]} onClick={function() { toggleSort(col[0]); }} style={{ padding: '8px 10px', textAlign: col[0] === 'name' ? 'left' : 'center', cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: 11 }}>{col[1]} {sortKey === col[0] ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>;
              })}
              <th style={{ padding: '8px 10px', textAlign: 'center', width: 60, fontSize: 11 }}>전환</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(function(co) {
              var planColors = { basic: '#3b82f6', pro: '#8b5cf6', trial: '#f59e0b', enterprise: '#10b981' };
              var plan = co.plan || 'basic';
              return (
                <tr key={co.id} style={{ borderBottom: '1px solid #f0f0ec', cursor: 'pointer' }} onClick={function() { switchTo(co.id); }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{co.name}<div style={{ fontSize: 10, color: '#888' }}>{co.bizNo || ''}</div></td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}><span style={{ background: (planColors[plan] || '#888') + '18', color: planColors[plan] || '#888', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{plan}</span></td>
                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>{co.carCount}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{co.monthOcr}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{co.pendingCount > 0 ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{co.pendingCount}</span> : '0'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{co.userCount}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center', fontSize: 11, color: '#888' }}>{co.lastActivity ? co.lastActivity.slice(0, 10) : '-'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}><button onClick={function(e) { e.stopPropagation(); switchTo(co.id); }} style={{ padding: '3px 10px', borderRadius: 5, border: '1px solid #d0d5dd', background: '#fff', cursor: 'pointer', fontSize: 10, fontWeight: 600, color: '#475569' }}>전환</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminOverview;
